/** `crm.finance` strings, kept apart from the main catalog to keep it small */
export const englishFinanceMessages = {
  title: "Finance",
  admin_only: "Finance is only available to the team admins.",
  filters: "Quick filters",
  search: "Search",
  none: "None",
  empty_filtered: "Nothing matches these filters.",
  tabs: {
    overview: "Overview",
    contracts: "Contracts",
    charges: "Receivables",
    expenses: "Expenses",
  },
  fields: {
    company: "Company",
    amount: "Amount",
    description: "Description",
    competence_month: "Competence month",
    competence_month_help: "Month the amount belongs to (any day of it)",
    due_date: "Due date",
    paid_at: "Paid on",
    status: "Status",
    notes: "Notes",
  },
  months: {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  },
  payment_methods: {
    pix: "Pix",
    boleto: "Boleto",
    transfer: "Bank transfer",
    card: "Card",
    other: "Other",
  },
  generate: {
    label: "Generate the month",
    done: "Created %{charges} charge(s) and %{expenses} expense(s)",
    nothing: "The month is already generated",
  },
  contracts: {
    create: "New contract",
    create_from_deal: "Create contract",
    edit: "Edit contract",
    created: "Contract created",
    updated: "Contract updated",
    deleted: "Contract deleted",
    empty: "No contract yet. Create one from a won deal or here.",
    per_month: "%{amount} / month",
    views: {
      active: "Active",
      paused: "Paused",
      cancelled: "Cancelled",
      all: "All",
    },
    statuses: {
      active: "Active",
      paused: "Paused",
      cancelled: "Cancelled",
    },
    adjustment_indexes: {
      none: "No adjustment",
      ipca: "IPCA",
      igpm: "IGP-M",
      fixed: "Fixed rate",
    },
    fields: {
      name: "Contract name",
      deal: "Deal",
      setup_amount: "Implementation fee",
      setup_amount_help: "One-off amount paid to start the project",
      setup_installments: "Implementation installments",
      monthly_amount: "Monthly fee",
      billing_day: "Billing day (1-28)",
      start_date: "Start date",
      end_date: "End date",
      adjustment_index: "Yearly adjustment",
      adjustment_month: "Adjustment month",
    },
  },
  charges: {
    create: "New charge",
    edit: "Edit charge",
    created: "Charge created",
    updated: "Charge updated",
    deleted: "Charge deleted",
    cancel: "Cancel charge",
    cancelled: "Charge cancelled",
    restore: "Restore charge",
    restored: "Charge restored",
    mark_sent: "Mark as sent",
    marked_sent: "Charge marked as sent",
    register_payment: "Register payment",
    payment_registered: "Payment registered",
    no_contract: "No contract (one-off)",
    empty:
      "No receivable yet. Generate the month to create the charges of your contracts.",
    count: "%{smart_count} charge |||| %{smart_count} charges",
    views: {
      open: "Open",
      overdue: "Overdue",
      due_soon: "Due in 7 days",
      not_sent: "Not sent",
      no_invoice: "Paid without NF",
      this_month: "This month",
      paid: "Paid",
      all: "All",
    },
    statuses: {
      pending: "To send",
      sent: "Sent",
      overdue: "Overdue",
      paid: "Paid",
      cancelled: "Cancelled",
    },
    kinds: {
      recurring: "Monthly fee",
      setup: "Implementation",
      one_off: "One-off",
    },
    fields: {
      contract: "Contract",
      kind: "Type",
      sent_at: "Sent on",
      paid_amount: "Amount received",
      payment_method: "Payment method",
      invoice: "NF",
      invoice_number: "NF number",
      invoice_files: "NF files (PDF / XML)",
      receipt_files: "Payment receipts",
    },
  },
  expenses: {
    title: "Payables",
    create: "New expense",
    edit: "Edit expense",
    created: "Expense created",
    updated: "Expense updated",
    deleted: "Expense deleted",
    mark_paid: "Mark as paid",
    marked_paid: "Expense marked as paid",
    empty: "No expense yet.",
    views: {
      open: "To pay",
      due_soon: "Due in 7 days",
      this_month: "This month",
      paid: "Paid",
      all: "All",
    },
    statuses: {
      to_pay: "To pay",
      late: "Late",
      paid: "Paid",
    },
    categories: {
      infrastructure: "Infrastructure",
      payroll: "Payroll",
      tools: "Tools / SaaS",
      taxes: "Taxes",
      marketing: "Marketing",
      accounting: "Accounting",
      other: "Other",
    },
    fields: {
      category: "Category",
      supplier: "Supplier",
      files: "NF / receipts",
    },
  },
  recurring: {
    title: "Recurring expenses",
    help: 'Created every month by "Generate the month".',
    create: "New recurring expense",
    edit: "Edit recurring expense",
    created: "Recurring expense created",
    updated: "Recurring expense updated",
    deleted: "Recurring expense deleted",
    empty: "No recurring expense yet (hosting, salaries, tools...).",
    total: "Fixed monthly cost: %{amount}",
    due_day: "due on the %{day}",
    inactive: "Inactive",
    fields: {
      due_day: "Due day (1-28)",
      active: "Active",
    },
  },
  alerts: {
    title: "Needs attention",
    none: "Nothing to do: everything is up to date.",
    severities: {
      error: "Urgent",
      warning: "Warning",
      info: "Information",
    },
    kinds: {
      overdue:
        "%{smart_count} overdue charge |||| %{smart_count} overdue charges",
      due_soon:
        "%{smart_count} charge due in 7 days |||| %{smart_count} charges due in 7 days",
      not_generated:
        "%{smart_count} charge of this month not generated |||| %{smart_count} charges of this month not generated",
      not_sent:
        "%{smart_count} charge not sent yet |||| %{smart_count} charges not sent yet",
      paid_without_invoice:
        "%{smart_count} payment without NF |||| %{smart_count} payments without NF",
      expenses_due:
        "%{smart_count} expense to pay |||| %{smart_count} expenses to pay",
      adjustment_due:
        "%{smart_count} contract to adjust this month |||| %{smart_count} contracts to adjust this month",
    },
  },
  overview: {
    empty:
      "No finance data yet. Create a contract (implementation fee + monthly fee) to see your predictable revenue.",
    error: "The finance data could not be loaded.",
    truncated:
      "Too many records: only the first 1000 of a list were used, some numbers are partial.",
    no_data: "No data for the period",
    show_table: "Show as a table",
    month: "Month",
    count: "Count",
    share: "Share",
    other: "Other",
    days_late: "Days late",
    charges_count: "%{smart_count} charge |||| %{smart_count} charges",
    share_of_mrr: "%{share} of the MRR",
    aging: {
      "1_15": "1-15 days",
      "16_30": "16-30 days",
      "31_60": "31-60 days",
      "61_plus": "60+ days",
    },
    series: {
      mrr: "MRR",
      billed: "Billed",
      received: "Received",
      expenses_paid: "Expenses paid",
      net: "Result",
      recurring: "Monthly fees and one-offs",
      setup: "Implementation",
      overdue: "Overdue",
    },
    charts: {
      mrr: "Monthly recurring revenue",
      mrr_help: "Sum of the monthly fees of the active contracts",
      billed_received: "Billed vs received",
      billed_received_help: "Charges of the month vs payments received in it",
      result: "Revenue, expenses and result",
      result_help: "Cash in and out of each month",
      mix: "Revenue mix",
      mix_help: "Received: recurring revenue vs implementation fees",
      forecast: "Forecast for the next 6 months",
      forecast_help:
        "Active contracts and remaining implementation installments",
      aging: "Overdue by age",
      aging_help: "Unpaid charges past their due date",
      expenses: "Expenses by category",
      expenses_help: "Last 12 months",
      concentration: "Revenue concentration",
      concentration_help: "MRR of the 10 biggest clients",
    },
    kpis: {
      mrr: "MRR",
      vs_last_month: "vs last month",
      received: "Received this month",
      of_billed: "of %{amount} billed",
      overdue: "Overdue",
      delinquency:
        "%{smart_count} charge · %{rate} of the due amount |||| %{smart_count} charges · %{rate} of the due amount",
      net: "Result this month",
      margin: "margin %{margin}",
      expenses: "Expenses this month",
      expenses_paid: "%{amount} already paid",
      arpa: "Average ticket",
      active_clients:
        "%{smart_count} active client |||| %{smart_count} active clients",
      setup_to_receive: "Implementation to receive",
      active_contracts:
        "%{smart_count} active contract |||| %{smart_count} active contracts",
      concentration: "Biggest client",
      concentration_help: "share of the MRR",
    },
  },
};
